import { useStudyStreak } from '../../../hooks/useStudyStreak';

interface StudyStreakProps {
  userId: string | undefined;
  layoutMode?: 'normal' | 'compact';
}

/**
 * 学習ストリーク表示コンポーネント
 * GitHubの草のようなヒートマップで学習履歴を可視化
 */
export default function StudyStreak({ userId, layoutMode = 'normal' }: StudyStreakProps) {
  const { heatmapData, currentStreak, longestStreak, totalDays, loading } =
    useStudyStreak(userId);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  // 週ごとにデータを分割（12週間分）
  const weeks: Array<Array<{ date: string; minutes: number }>> = [];
  for (let i = 0; i < heatmapData.length; i += 7) {
    weeks.push(heatmapData.slice(i, i + 7));
  }

  // 学習時間に応じた色の濃淡を決定
  const getColorClass = (minutes: number): string => {
    if (minutes === 0) return 'bg-gray-100 dark:bg-gray-700';
    if (minutes < 30) return 'bg-teal-200 dark:bg-teal-800';
    if (minutes < 60) return 'bg-teal-400 dark:bg-teal-600';
    if (minutes < 120) return 'bg-teal-600 dark:bg-teal-500';
    return 'bg-teal-700 dark:bg-teal-400';
  };

  // 曜日ラベル
  const dayLabels = ['日', '月', '火', '水', '木', '金', '土'];

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg ${layoutMode === 'compact' ? 'p-4' : 'p-6'}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`font-bold text-gray-800 dark:text-white flex items-center gap-2 ${layoutMode === 'compact' ? 'text-base' : 'text-lg'}`}>
          <i className="ri-fire-line text-orange-500"></i>
          学習ストリーク
        </h3>
        <span className={`bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 font-bold rounded-full ${layoutMode === 'compact' ? 'px-3 py-1 text-lg' : 'px-4 py-2 text-2xl'}`}>
          {currentStreak}日
        </span>
      </div>

      <div className={`space-y-3 ${layoutMode === 'compact' ? 'text-sm' : ''}`}>
        <div className="flex items-center justify-between">
          <span className="text-gray-600 dark:text-gray-400">最長記録</span>
          <span className="font-bold text-gray-800 dark:text-white">{longestStreak}日</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-600 dark:text-gray-400">総学習日数</span>
          <span className="font-bold text-gray-800 dark:text-white">{totalDays}日</span>
        </div>
      </div>

      {/* Calendar Heatmap */}
      <div className={layoutMode === 'compact' ? 'mt-4' : 'mt-6'}>
        <h4 className={`font-semibold text-gray-700 dark:text-gray-300 mb-3 ${layoutMode === 'compact' ? 'text-sm' : 'text-base'}`}>
          学習カレンダー
        </h4>
        <div className="grid grid-cols-7 gap-1">
          {heatmapData.slice(-30).map((day, index) => (
            <div
              key={index}
              className={`aspect-square rounded ${
                day.minutes > 0
                  ? 'bg-green-500 dark:bg-green-600'
                  : 'bg-gray-200 dark:bg-gray-700'
              } ${layoutMode === 'compact' ? '' : 'hover:scale-110'} transition-transform duration-200`}
              title={`${day.date}: ${day.minutes > 0 ? `${day.minutes}分学習` : '未学習'}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
